import { useCallback, useEffect, useRef, useState } from 'react';
import { setCallLivenessMasterTab } from '../services/callLivenessHeartbeat';

const CHANNEL_NAME = 'connect-widget-tab-sync';
const MASTER_LOCK_KEY = 'connect-widget-master-lock';
const CALL_STATE_KEY = 'connect-widget-call-active';
const AGENT_STATE_KEY = 'connect-widget-agent-state';
const HEARTBEAT_MS = 2000;
const STALE_MS = 7000;
// const MASTER_TAB_TITLE_PREFIX = '● ';
// const MASTER_TAB_TITLE_PREFIX = '🔴 ';
const MASTER_TAB_TITLE_PREFIX = '🔹';
const DEFAULT_BASE_TITLE = 'Adastra Connect';

const MSG = {
    FOCUS_MASTER: 'FOCUS_MASTER',
    CALL_STATE: 'CALL_STATE',
    AGENT_STATE: 'AGENT_STATE',
} as const;

export interface SyncedAgentState {
    isLoggedIn: boolean;
    agentStatus: string;
}

const DEFAULT_AGENT_STATE: SyncedAgentState = {
    isLoggedIn: false,
    agentStatus: 'Offline',
};

const CALL_STATUSES = ['Busy', 'CallingCustomer', 'PendingBusy', 'On call', 'Connected', 'Connecting'];

export function getConnectStatusColor(
    state: SyncedAgentState,
    callActive: boolean,
): string {
    if (!state.isLoggedIn) return '#A1A5B7';
    if (callActive || CALL_STATUSES.some((s) => state.agentStatus.toLowerCase().includes(s.toLowerCase()))) {
        return '#D7383C';
    }
    if (state.agentStatus === 'Available') return '#60BB1A';
    return '#A1A5B7';
}

export function getConnectStatusLabel(state: SyncedAgentState, callActive: boolean): string {
    if (!state.isLoggedIn) return 'Not signed in';
    if (callActive) return 'On call';
    return state.agentStatus || 'Offline';
}

interface MasterLock {
    tabId: string;
    lastHeartbeat: number;
}

function generateTabId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `tab-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function readMasterLock(): MasterLock | null {
    try {
        const raw = localStorage.getItem(MASTER_LOCK_KEY);
        if (!raw) return null;
        return JSON.parse(raw) as MasterLock;
    } catch {
        return null;
    }
}

function writeMasterLock(lock: MasterLock) {
    localStorage.setItem(MASTER_LOCK_KEY, JSON.stringify(lock));
}

function readSyncedCallActive(): boolean {
    try {
        const raw = localStorage.getItem(CALL_STATE_KEY);
        if (!raw) return false;
        return !!(JSON.parse(raw) as { active?: boolean }).active;
    } catch {
        return false;
    }
}

function writeSyncedCallActive(active: boolean) {
    localStorage.setItem(CALL_STATE_KEY, JSON.stringify({ active, ts: Date.now() }));
}

function readSyncedAgentState(): SyncedAgentState {
    try {
        const raw = localStorage.getItem(AGENT_STATE_KEY);
        if (!raw) return DEFAULT_AGENT_STATE;
        const parsed = JSON.parse(raw) as Partial<SyncedAgentState>;
        return {
            isLoggedIn: !!parsed.isLoggedIn,
            agentStatus: typeof parsed.agentStatus === 'string' ? parsed.agentStatus : 'Offline',
        };
    } catch {
        return DEFAULT_AGENT_STATE;
    }
}

function writeSyncedAgentState(state: SyncedAgentState) {
    localStorage.setItem(AGENT_STATE_KEY, JSON.stringify({ ...state, ts: Date.now() }));
}

/** Web Locks name — the browser releases it on reload, close, crash or navigation. */
const MASTER_WEB_LOCK_NAME = 'connect-widget-master';

/**
 * Minimal Web Locks typing. Declared locally because the project's TypeScript (4.6) DOM lib
 * does not expose `navigator.locks`.
 */
interface WebLock {
    name: string;
    mode: 'exclusive' | 'shared';
}
interface WebLockRequestOptions {
    ifAvailable?: boolean;
    signal?: AbortSignal;
}
interface WebLockManager {
    request<T>(
        name: string,
        options: WebLockRequestOptions,
        callback: (lock: WebLock | null) => T | Promise<T>,
    ): Promise<T>;
}

function getWebLockManager(): WebLockManager | null {
    if (typeof navigator === 'undefined') return null;
    const locks = (navigator as Navigator & { locks?: WebLockManager }).locks;
    return locks && typeof locks.request === 'function' ? locks : null;
}

function supportsWebLocks(): boolean {
    return getWebLockManager() !== null;
}

function isAbortError(e: unknown): boolean {
    return (e as { name?: string } | null)?.name === 'AbortError';
}

type ApplyRole = (master: boolean) => void;

/**
 * Primary election: an exclusive Web Lock held for the lifetime of the tab.
 * - Granted instantly when no other tab holds it (no stale-lock wait after a refresh).
 * - Released by the browser when the tab reloads/closes/crashes, so a queued tab takes
 *   over immediately.
 * - No heartbeat, so background-tab timer throttling cannot cause master flapping.
 */
function electWithWebLocks(applyRole: ApplyRole, onUnsupported: () => () => void): () => void {
    let disposed = false;
    let releaseHold: (() => void) | null = null;
    let fallbackTeardown: (() => void) | null = null;
    const queuedRequest = new AbortController();

    const holdUntilDisposed = (): Promise<void> => {
        if (disposed) return Promise.resolve();
        applyRole(true);
        return new Promise<void>((resolve) => {
            releaseHold = resolve;
        });
    };

    const fallBack = (e: unknown) => {
        if (disposed || isAbortError(e)) return;
        console.warn('[TabElection] Web Locks unavailable — falling back to localStorage lock', e);
        fallbackTeardown = onUnsupported();
    };

    const locks = getWebLockManager();
    if (!locks) {
        fallBack(new Error('navigator.locks unavailable'));
        return () => {
            disposed = true;
            fallbackTeardown?.();
        };
    }

    try {
        locks
            .request(MASTER_WEB_LOCK_NAME, { ifAvailable: true }, (lock: WebLock | null) => {
                if (lock) return holdUntilDisposed();
                if (disposed) return undefined;
                // Another tab is confirmed to hold the lock → observer now, master later.
                applyRole(false);
                locks
                    .request(MASTER_WEB_LOCK_NAME, { signal: queuedRequest.signal }, holdUntilDisposed)
                    .catch(fallBack);
                return undefined;
            })
            .catch(fallBack);
    } catch (e) {
        fallBack(e);
    }

    return () => {
        disposed = true;
        queuedRequest.abort();
        releaseHold?.();
        fallbackTeardown?.();
    };
}

/**
 * Fallback election for browsers without Web Locks: localStorage lock + heartbeat.
 * The lock is released on pagehide so a refresh does not wait out STALE_MS.
 */
function electWithStorageLock(tabId: string, applyRole: ApplyRole): () => void {
    const tryClaimMaster = (): boolean => {
        const now = Date.now();
        const lock = readMasterLock();
        if (!lock || now - lock.lastHeartbeat > STALE_MS) {
            writeMasterLock({ tabId, lastHeartbeat: now });
        }
        return readMasterLock()?.tabId === tabId;
    };

    const releaseIfOwned = () => {
        try {
            if (readMasterLock()?.tabId === tabId) localStorage.removeItem(MASTER_LOCK_KEY);
        } catch {
            /* storage unavailable */
        }
    };

    applyRole(tryClaimMaster());

    const onStorage = (e: StorageEvent) => {
        if (e.key === MASTER_LOCK_KEY) applyRole(tryClaimMaster());
    };
    const onPageShow = (e: PageTransitionEvent) => {
        if (e.persisted) applyRole(tryClaimMaster());
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('pagehide', releaseIfOwned);
    window.addEventListener('pageshow', onPageShow);

    const heartbeat = window.setInterval(() => {
        const lock = readMasterLock();
        if (lock?.tabId === tabId) {
            writeMasterLock({ tabId, lastHeartbeat: Date.now() });
        } else {
            applyRole(tryClaimMaster());
        }
    }, HEARTBEAT_MS);

    return () => {
        window.clearInterval(heartbeat);
        window.removeEventListener('storage', onStorage);
        window.removeEventListener('pagehide', releaseIfOwned);
        window.removeEventListener('pageshow', onPageShow);
        releaseIfOwned();
    };
}

export function useConnectTabElection() {
    const tabIdRef = useRef(generateTabId());
    /** null = election in progress, true = this tab owns CCP, false = another tab confirmed as master. */
    const [isMasterTab, setIsMasterTab] = useState<boolean | null>(null);
    const isMasterRef = useRef<boolean | null>(null);
    const [syncedCallActive, setSyncedCallActive] = useState(false);
    const [syncedAgentState, setSyncedAgentState] = useState<SyncedAgentState>(DEFAULT_AGENT_STATE);
    const channelRef = useRef<BroadcastChannel | null>(null);
    const baseTitleRef = useRef<string | null>(null);

    const getBaseTitle = useCallback((): string => {
        if (baseTitleRef.current) return baseTitleRef.current;
        const stripped = document.title.startsWith(MASTER_TAB_TITLE_PREFIX)
            ? document.title.slice(MASTER_TAB_TITLE_PREFIX.length)
            : document.title;
        baseTitleRef.current = stripped.trim() || DEFAULT_BASE_TITLE;
        return baseTitleRef.current;
    }, []);

    useEffect(() => {
        const bc = new BroadcastChannel(CHANNEL_NAME);
        channelRef.current = bc;

        const applyRole: ApplyRole = (master) => {
            isMasterRef.current = master;
            setIsMasterTab(master);
        };

        setSyncedCallActive(readSyncedCallActive());
        setSyncedAgentState(readSyncedAgentState());

        bc.onmessage = (event: MessageEvent) => {
            const data = event.data;
            if (!data || typeof data !== 'object') return;

            if (data.type === MSG.CALL_STATE) {
                setSyncedCallActive(!!data.active);
            }

            if (data.type === MSG.AGENT_STATE && data.state) {
                setSyncedAgentState({
                    isLoggedIn: !!data.state.isLoggedIn,
                    agentStatus: typeof data.state.agentStatus === 'string' ? data.state.agentStatus : 'Offline',
                });
            }

            if (data.type === MSG.FOCUS_MASTER && isMasterRef.current === true) {
                window.focus();
            }
        };

        const onStorage = (e: StorageEvent) => {
            if (e.key === CALL_STATE_KEY) {
                setSyncedCallActive(readSyncedCallActive());
            }
            if (e.key === AGENT_STATE_KEY) {
                setSyncedAgentState(readSyncedAgentState());
            }
        };
        window.addEventListener('storage', onStorage);

        const tabId = tabIdRef.current;
        const startStorageElection = () => electWithStorageLock(tabId, applyRole);
        const teardownElection = supportsWebLocks()
            ? electWithWebLocks(applyRole, startStorageElection)
            : startStorageElection();

        return () => {
            teardownElection();
            window.removeEventListener('storage', onStorage);
            bc.close();
            channelRef.current = null;
        };
    }, []);

    useEffect(() => {
        setCallLivenessMasterTab(isMasterTab === true);
    }, [isMasterTab]);

    // Mark the browser tab so interpreters can spot the Connect master tab at a glance.
    useEffect(() => {
        const baseTitle = getBaseTitle();

        if (isMasterTab === true) {
            document.title = `${MASTER_TAB_TITLE_PREFIX}${baseTitle}`;
        } else if (isMasterTab === false) {
            document.title = baseTitle;
        }

        return () => {
            if (isMasterTab === true && document.title.startsWith(MASTER_TAB_TITLE_PREFIX)) {
                document.title = baseTitle;
            }
        };
    }, [isMasterTab, getBaseTitle]);

    const publishCallActive = useCallback((active: boolean) => {
        writeSyncedCallActive(active);
        channelRef.current?.postMessage({ type: MSG.CALL_STATE, active });
    }, []);

    const publishAgentState = useCallback((state: SyncedAgentState) => {
        writeSyncedAgentState(state);
        channelRef.current?.postMessage({ type: MSG.AGENT_STATE, state });
    }, []);

    const requestMasterFocus = useCallback(() => {
        channelRef.current?.postMessage({ type: MSG.FOCUS_MASTER });
    }, []);

    return {
        isMasterTab,
        syncedCallActive,
        syncedAgentState,
        publishCallActive,
        publishAgentState,
        requestMasterFocus,
    };
}
